import {
  ArrowArcLeft,
  ArrowArcRight,
  BracketsCurly,
  MagicWand,
  Smiley,
} from "@phosphor-icons/react";
import { useCallback } from "react";

import EmojiSelector from "common/components/EmojiSelector";
import { Button } from "common/components/ui/Button";
import { extractAiDescriptors } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import Variables from "./Variables";

import type { Editor } from "@tiptap/react";

import MagicWandIcon from "assets/icons/magic-wand.svg?react";

interface ToolbarProps {
  editor: Editor;
  customVariables: { name: string; value: string }[];
}

export default function Toolbar({ editor, customVariables }: ToolbarProps) {
  const isDesktop = useTwBreakpoint("lg");

  const addAiVariable = useCallback(() => {
    const aiDescriptors = extractAiDescriptors(editor.getJSON().content);
    const existingIds = Object.keys(aiDescriptors);

    let newId: string;
    for (let i = 1; ; i += 1) {
      newId = `VAR_${i}`;
      if (!existingIds.includes(newId)) break;
    }

    editor
      .chain()
      .focus()
      .insertContent({
        type: "aiVariable",
        attrs: {
          id: newId,
          description: "",
        },
      })
      .run();
  }, []);

  const buttonSize = isDesktop ? "xs" : "md";

  return (
    <div className="flex items-center p-1 lg:p-2">
      <div className="flex items-center lg:gap-x-2">
        <EmojiSelector
          onEmojiSelection={(selectedEmoji) => {
            editor.chain().focus().insertContent(selectedEmoji).run();
          }}
          buttonProps={{
            size: buttonSize,
            children: <Smiley />,
          }}
        />

        <Variables customVariables={customVariables} editor={editor} />

        {/* Spintax */}
        <Button
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertContent("{SPIN}Option 1|Option 2|Option 3{ENDSPIN}")
              .run()
          }
          disabled={!editor.isEditable}
          size={buttonSize}
          variant="ghost-black"
          intent="iconOnly"
          aria-label="Spintax"
        >
          <BracketsCurly />
        </Button>

        {/* AI Variable */}
        <Button
          onClick={addAiVariable}
          disabled={!editor.isEditable}
          size={buttonSize}
          variant="ghost-black"
          intent="iconOnly"
          aria-label="Spintax"
        >
          {editor.isEditable ? <MagicWandIcon /> : <MagicWand />}
        </Button>
      </div>

      {/* Undo / Redo */}
      <div className="ml-auto flex items-center gap-x-1 lg:gap-x-2">
        <Button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo() || !editor.isEditable}
          size={buttonSize}
          variant="secondary-black"
          intent="iconOnly"
          aria-label="Undo"
        >
          <ArrowArcLeft />
        </Button>

        <Button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo() || !editor.isEditable}
          size={buttonSize}
          variant="secondary-black"
          intent="iconOnly"
          aria-label="Redo"
        >
          <ArrowArcRight />
        </Button>
      </div>
    </div>
  );
}
